import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserLock, faShieldAlt } from '@fortawesome/free-solid-svg-icons';

interface AuthRequiredAlertProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenAuth?: () => void;
  type?: 'quote' | 'carrier';
}

const AuthRequiredAlert: React.FC<AuthRequiredAlertProps> = ({
  isOpen,
  onClose,
  onOpenAuth,
  type = 'quote',
}) => {
  if (!isOpen) return null;

  const handleSignIn = () => {
    onClose();
    
    // Open auth modal
    if (onOpenAuth) {
      onOpenAuth();
    }
  };

  const getContent = () => {
    if (type === 'carrier') {
      return {
        title: 'Carrier Registration',
        message: 'Please sign in to register as a carrier. This helps us verify your identity and maintain a secure registry of all our carrier partners.',
        icon: faUserLock,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        buttonColor: 'bg-blue-600 hover:bg-blue-700'
      };
    }
    
    return {
      title: 'Quote Request',
      message: 'Please sign in to request a quote. This allows us to provide you with accurate pricing and ensures you can track your transport request.',
      icon: faShieldAlt,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      buttonColor: 'bg-green-600 hover:bg-green-700'
    };
  };

  const content = getContent();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all animate-in slide-in-from-bottom-4 duration-300">
        {/* Header with icon and close */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex items-center">
            <div className={`p-3 rounded-full ${content.bgColor} mr-4`}>
              <FontAwesomeIcon
                icon={content.icon}
                className={`${content.color} text-xl`}
              />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">
              {content.title}
            </h3>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-4">
          <p className="text-gray-600 leading-relaxed mb-4">
            {content.message}
          </p>

          {/* Benefits highlight */}
          <div className={`${content.bgColor} ${content.borderColor} border rounded-lg p-4 mb-6`}>
            <div className="flex items-center">
              <FontAwesomeIcon icon={faUserLock} className={`${content.color} mr-3`} />
              <div>
                <p className={`${content.color.replace('text-', 'text-')} font-medium text-sm`}>
                  Free registration • Takes less than 1 minute
                </p>
                <p className="text-gray-600 text-xs mt-1">
                  Secure authentication with Google or email
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 p-6 pt-0">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSignIn}
            className={`px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors duration-200 ${content.buttonColor} shadow-sm hover:shadow-md`}
          >
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthRequiredAlert;
